import { useState, useEffect, useRef, useCallback } from 'react';
import { Clip } from '../types';

interface ClipStreamingData {
  type: 'clip_generated';
  video_id: string;
  clip: Clip;
}

export interface UseClipStreamingReturn {
  clips: Clip[];
  isConnected: boolean;
  connect: (videoId: string) => void;
  disconnect: () => void;
  clearClips: () => void;
}

export const useClipStreaming = (): UseClipStreamingReturn => {
  const [clips, setClips] = useState<Clip[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const websocketRef = useRef<WebSocket | null>(null);
  const videoIdRef = useRef<string | null>(null);
  const connectionFailuresRef = useRef<number>(0);
  const lastConnectionAttemptRef = useRef<number>(0);

  const disconnect = useCallback(() => {
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
    }
    setIsConnected(false);
    videoIdRef.current = null;
    connectionFailuresRef.current = 0;
  }, []);

  const connect = useCallback((videoId: string) => {
    // Don't reconnect if already connected to the same video
    if (websocketRef.current && videoIdRef.current === videoId && isConnected) {
      // console.log(`🔌 [ClipStreaming] Already connected to video ${videoId}`);
      return;
    }

    // Implement exponential backoff for failed connections
    const now = Date.now();
    const timeSinceLastAttempt = now - lastConnectionAttemptRef.current;
    const backoffTime = Math.min(1000 * Math.pow(2, connectionFailuresRef.current), 30000); // Max 30 seconds
    
    if (connectionFailuresRef.current > 0 && timeSinceLastAttempt < backoffTime) {
      // console.log(`⏳ [ClipStreaming] Waiting ${backoffTime - timeSinceLastAttempt}ms before reconnecting (failures: ${connectionFailuresRef.current})`);
      return;
    }

    // Disconnect existing connection
    disconnect();
    
    lastConnectionAttemptRef.current = now;

    try {
      // Get WebSocket URL - CRITICAL FIX for URL mismatch
      const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
      
      let wsUrl: string;
      if (baseUrl.includes('stream-clipperz.web.app') || baseUrl.includes('clipthis.io')) {
        // Use the CORRECT current backend URL for production
        wsUrl = `wss://streamclipper-backend-rnf5px4hsq-uc.a.run.app/api/v1/clips/stream/${videoId}`;
      } else if (baseUrl === window.location.origin && window.location.origin.includes('stream-clipperz.web.app')) {
        // Fallback for Firebase hosting detection
        wsUrl = `wss://streamclipper-backend-rnf5px4hsq-uc.a.run.app/api/v1/clips/stream/${videoId}`;
      } else {
        // For local development, use local WebSocket
        wsUrl = baseUrl.replace('http://', 'ws://').replace('https://', 'wss://') + `/api/v1/clips/stream/${videoId}`;
      }

      // console.log(`🔌 [ClipStreaming] Connecting to WebSocket for video ${videoId}:`, wsUrl);
      // console.log(`🔌 [ClipStreaming] Base URL detected:`, baseUrl);
      // console.log(`🔌 [ClipStreaming] Window origin:`, window.location.origin);
      
      const ws = new WebSocket(wsUrl);
      websocketRef.current = ws;
      videoIdRef.current = videoId;

      ws.onopen = () => {
        // console.log(`✅ [ClipStreaming] WebSocket connected successfully for video ${videoId}`);
        setIsConnected(true);
        connectionFailuresRef.current = 0; // Reset failure count on successful connection
      };

      ws.onmessage = (event) => {
        try {
          // console.log(`📨 [ClipStreaming] Raw WebSocket message received:`, event.data);
          const data: ClipStreamingData = JSON.parse(event.data);
          // console.log(`📨 [ClipStreaming] Parsed clip streaming data:`, data);

          if (data.type === 'clip_generated' && data.clip) {
            // console.log(`🎬 [ClipStreaming] New clip received: ${data.clip.title} (${data.clip.id})`);
            
            // Ensure clip has required fields with defaults
            const safeClip = {
              ...data.clip,
              tags: data.clip.tags || []  // Ensure tags is always an array
            };
            
            // Add the new clip to the list
            setClips(prevClips => {
              // Check if clip already exists (avoid duplicates)
              const exists = prevClips.some(clip => clip.id === safeClip.id);
              if (exists) {
                // console.log(`⚠️ [ClipStreaming] Clip ${safeClip.id} already exists, skipping`);
                return prevClips;
              }
              
              // Add new clip and sort by start_time
              const newClips = [...prevClips, safeClip];
              const sortedClips = newClips.sort((a, b) => a.start_time - b.start_time);
              // console.log(`✅ [ClipStreaming] Added clip ${safeClip.id}, total clips: ${sortedClips.length}`);
              return sortedClips;
            });
          } else {
            // console.log(`⚠️ [ClipStreaming] Received message with unknown type or missing clip:`, data);
          }
        } catch (err) {
          console.error(`❌ [ClipStreaming] Failed to parse WebSocket message:`, err, 'Raw data:', event.data);
        }
      };

      ws.onerror = (error) => {
        console.error(`❌ [ClipStreaming] WebSocket error for video ${videoId}:`, error);
        setIsConnected(false);
        connectionFailuresRef.current++;
      };

      ws.onclose = (event) => {
        // console.log(`🔌 [Clip Streaming] WebSocket closed for video ${videoId}. Code: ${event.code}, Reason: ${event.reason || 'No reason provided'}`);
        setIsConnected(false);
        websocketRef.current = null;
        videoIdRef.current = null;
        
        // Increment failure count on abnormal closure
        if (event.code !== 1000) { // 1000 is normal closure
          connectionFailuresRef.current++;
          // console.log(`❌ [ClipStreaming] Abnormal WebSocket closure, failure count: ${connectionFailuresRef.current}`);
        } else {
          // console.log(`✅ [ClipStreaming] Normal WebSocket closure`);
        }
      };

    } catch (error) {
      console.error(`❌ [ClipStreaming] Failed to connect to WebSocket for video ${videoId}:`, error);
      setIsConnected(false);
      connectionFailuresRef.current++;
    }
  }, [disconnect, isConnected]);

  const clearClips = useCallback(() => {
    setClips([]);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    clips,
    isConnected,
    connect,
    disconnect,
    clearClips
  };
}; 